import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, concatMap, tap} from 'rxjs/operators';
import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {SortTableCellTags} from '../../../../../shared/models/sort-table/cell-classes';
import {BaseCellComponent} from '../_base-cell/base-cell.component';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {PillListComponent} from '../../../pill-list/pill-list.component';
import {PillItem} from '../../../../../shared/models/pill';
import {TagInputComponent} from '../../../tag-input/tag-input.component';
import {TagService} from '../../../../../core/services/tag.service';
import {TilesService} from '../../../../../core/services/tiles.service';
import {ObjectType} from '../../../../../shared/models/object.model';
import {Tag} from '../../../../../shared/models/tag';
import {PermissionsService} from '../../../../../core/services/permissions.service';
import {SessionService} from '../../../../../core/services/session.service';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {Permission} from '../../../../../shared/models/fga/permission';

@Component({
  selector: 'motn-sort-table-cell-tags',
  standalone: true,
  templateUrl: './sort-table-cell-tags.component.html',
  styleUrls: ['./sort-table-cell-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PillListComponent,
    TagInputComponent,
  ],
})
export class SortTableCellTagsComponent extends BaseCellComponent implements OnInit {
  @Input() cell: SortTableCellTags;

  readonly overlayPositions: ConnectedPosition[] = [
    {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4},
    {originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -4},
  ];

  readonly objectType = ObjectType.TILE;
  editing = false;

  private confirmedTags: Tag[] = [];
  private readonly saveQueue$ = new Subject<Tag[]>();

  constructor(
    private tagService: TagService,
    private tilesService: TilesService,
    private permissionsService: PermissionsService,
    private session: SessionService,
    private shadeTools: ShadeToolsService,
  ) {
    super();
    this.saveQueue$.pipe(
      concatMap(tags => this.save$(tags)),
    ).subscribe();
  }

  startEditing(): void {
    if (this.editing) return;
    this.permissionsService.hasPermissions({
      UserID: this.session.userId,
      ObjectID: this.cell.tileId,
      ObjectType: ObjectType.TILE,
      Permissions: [Permission.CAN_CONFIGURE],
    }).subscribe({
      next: allowed => {
        if (!allowed) {
          this.shadeTools.errorDialog('You do not have permission to configure this tile.');
          return;
        }
        this.confirmedTags = this.cell.tags ?? [];
        this.editing = true;
      },
      error: () => this.shadeTools.errorDialog(),
    });
  }

  stopEditing(): void {
    this.editing = false;
  }

  onTagsChanged(tags: Tag[]): void {
    this.cell.tags = tags;
    this.tilesService.updateTileTags(this.cell.tileId, [...tags, ...this.facets()]);
    this.saveQueue$.next(tags);
  }

  private save$(tags: Tag[]): Observable<unknown> {
    const confirmed = this.confirmedTags;
    const facets = this.facets();
    const toAssignments = (list: Tag[]) => list.map(t => ({TagID: t.ID, Value: t.Value}));

    return this.tagService.setObjectTags$(
      ObjectType.TILE,
      this.cell.tileId,
      toAssignments([...confirmed, ...facets]),
      toAssignments([...tags, ...facets]),
    ).pipe(
      tap(() => this.confirmedTags = tags),
      catchError(() => {
        this.cell.tags = confirmed;
        this.tilesService.updateTileTags(this.cell.tileId, [...confirmed, ...facets]);
        this.shadeTools.errorDialog();
        return EMPTY;
      }),
    );
  }

  private facets(): Tag[] {
    return this.tilesService.sessUserTiles[this.cell.tileId]?.facets ?? [];
  }

  get pillItems(): PillItem[] {
    return (this.cell.tags ?? [])
      .slice()
      .sort((a, b) => a.Label.localeCompare(b.Label))
      .map(t => ({id: t.ID, label: t.Label}));
  }

  protected onInit(): void {}
}
